import { captureException } from "@sentry/nextjs";
import { IncomingMessage, ServerResponse } from "http";
import { NextRequest, NextResponse } from "next/server";

import { log } from "@voxmedia/duet-utils/logging";

// default cache-control setting for server rendered pages
export const CACHE_CONTROL = "no-store";
// cache-control setting for server rendered pages that should not be cached
export const CACHE_CONTROL_NO_CACHE = "no-cache, no-store, must-revalidate";
// default stale setting for surrogate-control headers of server rendered pages (1 day SWR, 1 week stale-if-error)
export const SURROGATE_CONTROL_STALE =
  "stale-while-revalidate=86400, stale-if-error=604800";
export const SURROGATE_CONTROL_STALE_5_SECONDS =
  "stale-while-revalidate=5, stale-if-error=604800";
export const SURROGATE_CONTROL_STALE_15_SECONDS =
  "stale-while-revalidate=15, stale-if-error=604800";
export const SURROGATE_CONTROL_STALE_30_SECONDS =
  "stale-while-revalidate=30, stale-if-error=604800";
export const SURROGATE_CONTROL_STALE_60_SECONDS =
  "stale-while-revalidate=60, stale-if-error=604800";
export const SURROGATE_CONTROL_1_MINUTE = "max-age=60";
export const SURROGATE_CONTROL_5_MINUTES = "max-age=300";
export const SURROGATE_CONTROL_15_MINUTES = "max-age=900";
export const SURROGATE_CONTROL_1_HOUR = "max-age=3600";
export const SURROGATE_CONTROL_1_DAY = "max-age=86400";
export const SURROGATE_CONTROL_7_DAYS = "max-age=604800";
export const SURROGATE_NO_STORE = "private, no-store";

const DUET_PREFIX = "d:";

/**
 * Adds cache prefixes to the given array of keys.
 *
 * The Duet cache prefix, e.g. `d:`, distinguishes between Duet and API cache keys.
 * The Vox Media cache prefix, e.g. `vx2:`, distinguishes between each community/wp site
 */
export const addCachePrefix = ({
  keys,
  vmPrefix,
  duetPrefix = false,
}: {
  keys: string[];
  vmPrefix?: string;
  duetPrefix?: boolean;
}): string[] =>
  keys.map(
    (key) =>
      `${duetPrefix ? DUET_PREFIX : ""}${vmPrefix ? vmPrefix : ""}${key}`,
  );

/**
 * Retrieves the surrogate keys for a page, combining global cache keys, community-specific keys,
 * additional keys passed from the page, and keys derived from GraphQL API query responses.
 *
 * @param vmPrefix The cache key prefix for the specific community.
 * @param pageKeys Additional surrogate keys passed in from the page.
 * @param queryResponseKeys Surrogate keys derived from the GraphQL API query responses via x-graphql-keys.
 * @returns An array of surrogate keys for the page.
 */
export const getPageSurrogateKeys = ({
  vmPrefix,
  pageKeys = [],
  queryResponseKeys = [],
}: {
  vmPrefix: string;
  pageKeys?: string[];
  queryResponseKeys?: string[];
}): string[] => {
  /**
   * Global cache keys used on all pages without a community specific prefix.
   * Purging these will purge across all communities on a network. These
   * do not get the duet prefix as `duet` is already intended to be the
   * key for duet on an entire network, `all` is the key for everything the
   * entire network (duet responses, api responses, anything Fastly caches).
   */
  const globalNetworkKeys = ["all", "duet"];
  /**
   * The same keys as above with the community specific "vm prefix" to scope purge
   * requests to a single community.
   */
  const globalCommunityKeys = addCachePrefix({
    keys: globalNetworkKeys,
    vmPrefix,
    duetPrefix: false,
  });

  const keysArray = [
    ...globalNetworkKeys,
    ...globalCommunityKeys,
    /**
     * NOTE: Query response keys already have appropriate prefixes applied
     * via getQueryResponseSurrogateKeys either in the query fetcher for
     * queries to the WordPress GQL API or via resource middleware for the
     * REST API requests to resolve the resource for responses returned
     * from middleware.
     */
    ...queryResponseKeys,
  ];

  /**
   * If additional keys were passed from the page generate two versions. One
   * without prefix and one with the vmPrefix.
   */
  if (pageKeys.length) {
    // No prefix version
    keysArray.push(...pageKeys);
    // VM prefix version
    keysArray.push(
      ...addCachePrefix({ keys: pageKeys, vmPrefix, duetPrefix: false }),
    );
  }

  return keysArray;
};

/**
 * Retrieves the surrogate keys for API responses, combining global cache keys, community-specific keys,
 * and additional keys passed from the API handler.
 *
 * @param vmPrefix The cache key prefix for the specific community.
 * @param apiHandlerKeys Additional surrogate keys passed in from the API handler.
 * @param queryResponseKeys Surrogate keys derived from query responses.
 * @param apiPrefix Optional prefix to add to API handler keys (e.g., "cyclone" for cyclone API keys).
 * @param addVmPrefixToQueryResponseKeys Whether to add vmPrefix to query response keys (default: true).
 * @returns An array of surrogate keys for the API response.
 */
export const getApiHandlerSurrogateKeys = ({
  vmPrefix,
  apiHandlerKeys = [],
  queryResponseKeys = [],
  apiPrefix,
  addVmPrefixToQueryResponseKeys = true,
}: {
  vmPrefix: string;
  apiHandlerKeys?: string[];
  queryResponseKeys?: string[];
  apiPrefix?: string;
  addVmPrefixToQueryResponseKeys?: boolean;
}): string[] => {
  /**
   * Global cache keys used on all API responses. Uses "api" instead of "duet"
   * to differentiate API responses from page responses. If an apiPrefix is provided,
   * it's also added as a global key to enable domain-specific purging.
   */
  const globalNetworkKeys = ["all", "api", ...(apiPrefix ? [apiPrefix] : [])];
  /**
   * The same keys as globalNetworkKeys with the community specific "vm prefix" to scope purge
   * requests to a single community.
   */
  const globalCommunityKeys = addCachePrefix({
    keys: globalNetworkKeys,
    vmPrefix,
    duetPrefix: false,
  });

  const keysArray = [
    ...globalNetworkKeys,
    ...globalCommunityKeys,
    // Query response keys - optionally add vmPrefix for API handlers
    ...(addVmPrefixToQueryResponseKeys
      ? addCachePrefix({
          keys: queryResponseKeys,
          vmPrefix,
          duetPrefix: false,
        })
      : queryResponseKeys),
  ];

  /**
   * If additional keys were passed from the api handler, generate two versions.
   * One without a prefix and one with the vmPrefix.
   */
  if (apiHandlerKeys.length) {
    if (apiPrefix) {
      // Apply custom prefix directly before the key name
      const prefixedKeys = apiHandlerKeys.map((key) => `${apiPrefix}:${key}`);
      const vmPrefixedKeys = apiHandlerKeys.map(
        (key) => `${vmPrefix}${apiPrefix}:${key}`,
      );
      keysArray.push(...prefixedKeys, ...vmPrefixedKeys);
    } else {
      // No custom prefix, just generate two versions
      // No prefix version
      keysArray.push(...apiHandlerKeys);
      // VM prefix version
      keysArray.push(
        ...addCachePrefix({
          keys: apiHandlerKeys,
          vmPrefix,
          duetPrefix: false,
        }),
      );
    }
  }

  return keysArray;
};

/**
 * Retrieves surrogate keys from the headers of a GraphQL or REST query responses from the
 * WordPress API.
 *
 * @param headers The headers from the GraphQL or REST query response returned from the API.
 * @param queryType The type of query response, either "gql" for GraphQL or "rest" for REST.
 * @param options Options for how surrogate keys should be prefixed.
 * @returns An array of surrogate keys for the query response.
 */
export const getQueryResponseSurrogateKeys = (
  // Headers from the GraphQL or REST query response returned from the API
  headers?: { [header: string]: string },
  queryType: "gql" | "rest" = "gql",
  { duetPrefix = true }: { duetPrefix?: boolean } = {},
): string[] => {
  const headerKey = queryType === "gql" ? "x-graphql-keys" : "surrogate-key";
  const headerValue = headers?.[headerKey];

  if (typeof headerValue !== "string") {
    return [];
  }

  const keys = headerValue.split(" ").filter((key) =>
    queryType === "gql"
      ? // Remove :graphql: substring keys and "wp" keys for GraphQL queries
        !(key.includes(":graphql:") || key === "wp" || key.includes(":wp"))
      : // Remove "wp" and "rest" keys for REST queries
        !(
          key === "wp" ||
          key === "rest" ||
          key.endsWith(":wp") ||
          key.endsWith(":rest")
        ),
  );

  return addCachePrefix({ keys, duetPrefix });
};

export const getSurrogateKeyHeader = ({
  headers, // Headers from the GraphQL query response returned from the API
  additionalKeys = "", // Additional keys passed in from the page
  vmPrefix,
}: {
  headers?: { [header: string]: string };
  additionalKeys?: string;
  vmPrefix: string;
}): string => {
  /**
   * Global cache keys used on all pages without a community specific prefix.
   * Purging these will purge across all communities on a network. These
   * do not get the duet prefix as `duet` is already intended to be the
   * key for duet on an entire network, `all` is the key for everything the
   * entire network (duet responses, api responses, anything Fastly caches).
   */
  const globalNetworkKeys = ["all", "duet"];
  /**
   * The same keys as above with the community specific "vm prefix" to scope purge
   * requests to a single community.
   */
  const globalCommunityKeys = addCachePrefix({
    keys: globalNetworkKeys,
    vmPrefix,
    duetPrefix: false,
  });

  const keysArray = [...globalNetworkKeys, ...globalCommunityKeys];

  if (headers && typeof headers["x-graphql-keys"] === "string") {
    const gqlKeys = headers["x-graphql-keys"]
      // Remove substrings containing :graphql:
      .replace(/\S*(:graphql:)\S*/g, "")
      .replace(/\s+/g, " ")
      .split(" ");

    // Add duet prefix to the graphql keys returned from the API
    keysArray.push(...addCachePrefix({ keys: gqlKeys, duetPrefix: true }));
  }

  if (additionalKeys.length) {
    // Add duet and vm prefix to additional keys passed in from the page
    keysArray.push(
      ...addCachePrefix({
        keys: additionalKeys.split(" "),
        vmPrefix,
        duetPrefix: true,
      }),
    );
  }

  return keysArray.join(" ");
};

export interface ResponseWithHeaders {
  setHeader: (h: string, v: string) => void;
  getHeader: (h: string) => number | string | string[] | null | undefined;
}

/**
 * Creates a ResponseWithHeaders wrapper for NextResponse objects.
 * This allows NextResponse objects to be used with setCacheHeaders.
 */
export const getSettableNextResponse = (
  res: NextResponse<unknown>,
): ResponseWithHeaders => ({
  getHeader: (h: string) => res.headers.get(h),
  setHeader: (h: string, v: string) => res.headers.set(h, v),
});

/**
 * Sets cache-related headers on the HTTP response object.
 *
 * @param res The HTTP response object (ServerResponse<IncomingMessage>).
 * @param vmPrefix The cache key prefix for the specific community.
 * @param cacheControl The Cache-Control header value.
 * @param maxAge The max-age directive value for Cache-Control header.
 * @param stale The stale directive value for Surrogate-Control header.
 * @param preview Indicates if the page is in preview mode.
 * @param pageKeys Surrogate keys passed in from a page's data fetching function.
 * @param apiHandlerKeys Additional surrogate keys passed in from an api handler.
 * @param apiPrefix Optional prefix to add to API handler keys (e.g., "cyclone" for cyclone API keys).
 * @param queryResponseKeys Surrogate keys derived from the GraphQL API query responses via x-graphql-keys.
 * @param addVmPrefixToQueryResponseKeys Whether to add vmPrefix to query response keys for API handlers
 * - (default: true, this can be set to false when keys are already expected to be globally unique).
 * @param exactKeys Keys that should be added directly without any modification or prefixing.
 * @param secondsToNextSponsorshipTransition Seconds until the next editorial sponsorship ends or begins.
 */
export const setCacheHeaders = ({
  res,
  vmPrefix,
  cacheControl,
  maxAge,
  stale,
  preview,
  pageKeys = [],
  apiHandlerKeys = [],
  apiPrefix,
  queryResponseKeys = [],
  addVmPrefixToQueryResponseKeys = true,
  exactKeys = [],
  secondsToNextSponsorshipTransition,
}: {
  res: ResponseWithHeaders;
  vmPrefix: string;
  cacheControl?: string;
  maxAge?: string;
  stale?: string;
  preview?: boolean;
  pageKeys?: string[];
  apiHandlerKeys?: string[];
  apiPrefix?: string;
  queryResponseKeys?: string[];
  addVmPrefixToQueryResponseKeys?: boolean;
  exactKeys?: string[];
  secondsToNextSponsorshipTransition?: number;
}): void => {
  /**
   * In preview mode we ensure the page is not cached in Fastly and return
   * early because surrogate keys are unnecessary for uncached responses.
   */
  if (preview) {
    res.setHeader("cache-control", CACHE_CONTROL_NO_CACHE);
    res.setHeader("surrogate-control", SURROGATE_NO_STORE);
    return;
  }

  // Determine which function to use based on which keys are provided
  let surrogateKeys: string[];
  if (apiHandlerKeys.length > 0) {
    // Use API surrogate keys function
    surrogateKeys = getApiHandlerSurrogateKeys({
      vmPrefix,
      apiHandlerKeys,
      queryResponseKeys,
      apiPrefix,
      addVmPrefixToQueryResponseKeys,
    });
  } else {
    // Use page surrogate keys function (default)
    surrogateKeys = getPageSurrogateKeys({
      vmPrefix,
      pageKeys,
      queryResponseKeys,
    });
  }

  // Add exact keys without any modification
  surrogateKeys.push(...exactKeys);

  const surrogateKeyHeader = res.getHeader("surrogate-key");
  /**
   * Add any existing surrogate keys to the array in cases where they were set
   * somewhere else in the response lifecycle. Namely this happens in _app.
   */
  if (surrogateKeyHeader && typeof surrogateKeyHeader === "string") {
    surrogateKeys.push(...surrogateKeyHeader.split(" "));
  }
  // Deduplicate and sort surrogate keys
  const dedupedAndSortedSurrogateKey = sortKeysByPrefix(
    [...new Set(surrogateKeys)],
    vmPrefix,
  ).join(" ");

  // Get the max-age number from the max-age portion of the surrogate-control header
  const maxAgeNumber = Number(
    (maxAge || SURROGATE_CONTROL_1_HOUR).replace("max-age=", ""),
  );
  /**
   * Set the surrogate-control max-age to the minimum of the maxAge header or
   * the seconds to next sponsorship transition. This ensures that the page
   * is refetched as soon as possible when a sponsorship transition is about
   * to occur so any server-side logic in regard to the sponsorship is executed.
   */
  const surrogateControlMaxAge = `max-age=${Math.min(
    maxAgeNumber,
    secondsToNextSponsorshipTransition || Infinity,
  )}`;

  res.setHeader("cache-control", cacheControl || CACHE_CONTROL);
  res.setHeader(
    "surrogate-control",
    `${surrogateControlMaxAge}, ${stale || SURROGATE_CONTROL_STALE}`,
  );
  res.setHeader("surrogate-key", dedupedAndSortedSurrogateKey);
};

/**
 * Sets cache related headers to ensure page is not cached
 *
 * @param res The HTTP response object (ServerResponse<IncomingMessage>).
 */
export const setNoCacheHeaders = (res: ServerResponse<IncomingMessage>) => {
  res.setHeader("cache-control", CACHE_CONTROL_NO_CACHE);
  res.setHeader("surrogate-control", SURROGATE_NO_STORE);
  res.removeHeader("Surrogate-Key");
};

/**
 * This sorting function is intended to give priority weight to cache keys
 * based on certain criteria, such as which prefix they have, and will sort
 * higher priority keys to the front of the array. This both makes it easier
 * to scan the keys for dev purposes, but also ensures that the most important
 * keys are always present in the cache headers in case we were to hit the
 * Fastly surrogate-key header byte limit (16,384 bytes). In my testing of the
 * homepage so far I have hit 2,197 bytes so we should be relatively safe but
 * with these changes we provide both a better experience for dev and testing
 * as well as better prevention of missed invalidations for important data
 * should we ever exceed the byte limit.
 *
 * https://www.fastly.com/documentation/reference/http/http-headers/Surrogate-Key/#:~:text=Individual%20keys%20are%20limited%20to,same%20header%20will%20be%20ignored.
 */
export function sortKeysByPrefix(keys: string[], vmPrefix: string): string[] {
  const bothPrefixes = `${DUET_PREFIX}${vmPrefix}`;

  // NOTE: priorityGroup is unused but included because it simplifies debugging for devx
  const priorityMap = {
    default: {
      priority: 100,
      priorityGroup: "default",
      sortLexicographically: true,
    },
    // If it has no prefix at all it should be first, e.g. `duet` `all`
    noPrefix: {
      priority: 1,
      priorityGroup: "noPrefix",
      sortLexicographically: true,
    },
    // If it has just the vm prefix, it should come second, e.g. `vx2:all`
    vmPrefix: {
      priority: 2,
      priorityGroup: "vmPrefix",
      sortLexicographically: true,
    },
    // Everything else after this should have both the duet and vm prefix
    duetPageKey: {
      priority: 3,
      priorityGroup: "duetPageKey",
      sortLexicographically: true,
    },
    // Query ID cache keys should come third
    queryId: {
      priority: 4,
      priorityGroup: "queryId",
      sortLexicographically: false,
    },
    /**
     * The next two priorities are for operation keys:
     * e.g. `operation:GlobalDataContainerQuery`
     *
     * If the operation is a layout query it should come after other operations,
     * e.g. `operation:HomepageLayoutQuery`
     */
    operation: {
      priority: 5,
      priorityGroup: "operation",
      sortLexicographically: false,
    },
    operationLayout: {
      priority: 6,
      priorityGroup: "operationLayout",
      sortLexicographically: false,
    },
    // Custom cache keys which are typically global e.g. `ck:breakingNewsBanner`
    customKey: {
      priority: 7,
      priorityGroup: "customKey",
      sortLexicographically: true,
    },
    // Resource keys e.g. `post:1234`
    resource: {
      priority: 8,
      priorityGroup: "resource",
      sortLexicographically: true,
    },
    // Media resource keys e.g. `image:1234`
    mediaResource: {
      priority: 9,
      priorityGroup: "mediaResource",
      sortLexicographically: true,
    },
    /**
     * NOTE: As of right now these are moved to lower priority, we should evaluate if we need
     * them at all. If we do we probably want to move them to a higher priority, but I expect
     * we can remove them entirely and just filter these on the WP side.
     * */
    // List keys set by WPGraphQL when lists of nodes are queried
    list: {
      priority: 10,
      priorityGroup: "list",
      sortLexicographically: true,
    },
  };

  const getPriority = (key: string) => {
    if (key.startsWith(vmPrefix)) {
      return priorityMap.vmPrefix;
    }
    if (key.startsWith(bothPrefixes)) {
      const unprefixedKey = key.replace(bothPrefixes, "");

      // Regular expression pattern to match a hexadecimal string used for queryId
      const queryIdPattern = /^[0-9a-fA-F]+$/;
      // Query ID keys
      if (queryIdPattern.test(unprefixedKey)) {
        return priorityMap.queryId;
      }
      // This check is after queryId patterb check because query ids have no `:`
      if (!unprefixedKey.includes(":")) {
        // Keys set in the Duet Page data fetching function
        return priorityMap.duetPageKey;
      }
      // Operation keys
      if (unprefixedKey.startsWith("operation:")) {
        return unprefixedKey.includes("LayoutQuery")
          ? priorityMap.operationLayout
          : priorityMap.operation;
      }
      // Custom keys
      if (unprefixedKey.startsWith("ck:")) {
        return priorityMap.customKey;
      }

      const [resourceType, wpId] = unprefixedKey.split(":");
      /**
       * Resource type keys
       *
       * This check verifies that this is a resource cache key which should always
       * follow the pattern `resourceType:wpId` where wpId is a number.
       */
      if (resourceType && wpId && !isNaN(Number(wpId))) {
        // Resource keys for media resources such as images, gifs, and videos
        if (resourceType === "image" || resourceType === "volumeVideo") {
          return priorityMap.mediaResource;
        }
        return priorityMap.resource;
      }
      // WPGraphQL list keys
      if (unprefixedKey.startsWith("list:")) {
        return priorityMap.list;
      }
      // If it has both prefixes, and isn't handled by other cases it should be last
      return priorityMap.default;
    }
    // If it has no prefix at all it should be first, e.g. `duet` `all`
    return priorityMap.noPrefix;
  };

  /**
   * Compare the prefix values and evaluate weighted priority, if they are equal
   * in priority they should remain unmoved. If it is a custom key, duet page key,
   * resource key or anything lower priority for both a and b sort lexicographically.
   */
  return keys.sort((a, b) => {
    const keyA = getPriority(a);
    const keyB = getPriority(b);

    if (keyA.priority === keyB.priority && keyA.sortLexicographically) {
      // Sort lexicographically
      return a.localeCompare(b);
    }

    return keyA.priority - keyB.priority;
  });
}

export const bypassWpCacheEnabled = (
  req: IncomingMessage | NextRequest,
): boolean => {
  if ("headers" in req) {
    let bypassHeader;

    if (typeof req.headers.get === "function") {
      bypassHeader = req.headers.get("vm-cache-bypass-wp");
    }
    if ("vm-cache-bypass-wp" in req.headers) {
      bypassHeader = req.headers["vm-cache-bypass-wp"] as string;
    }

    if (bypassHeader) {
      return true;
    }
  }

  const baseUrl =
    "headers" in req && "host" in req.headers
      ? `http://${req.headers.host}`
      : req.url;
  const bypassWpCacheParam =
    req.url && new URL(req.url, baseUrl).searchParams.get("bypassWpCache");

  return !!bypassWpCacheParam;
};

export const purgeSurrogateKeys = async ({
  surrogateKeys,
  soft = false,
  context = "caching",
}: {
  surrogateKeys: string[];
  soft?: boolean;
  context?: string;
}) => {
  const serviceId = process.env.FASTLY_SERVICE_ID;
  const apiKey = process.env.FASTLY_API_KEY;
  const deployEnv = process.env.NEXT_PUBLIC_DEPLOY_ENV;

  try {
    if (deployEnv !== "ci" && deployEnv !== "dev" && (!serviceId || !apiKey)) {
      throw new Error("Fastly credentials unavailable");
    }

    const response = await fetch(
      `https://api.fastly.com/service/${serviceId}/purge`,
      {
        method: "POST",
        headers: {
          "Fastly-Key": apiKey || "",
          "Fastly-Soft-Purge": soft ? "1" : "0",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          surrogate_keys: surrogateKeys,
        }),
      },
    );

    if (response.ok) {
      log.info(
        `Successfully purged Fastly cache for surrogateKeys: ${JSON.stringify(surrogateKeys)}`,
      );

      return true;
    } else {
      throw new Error(
        `Failed to purge Fastly cache for surrogateKeys: ${JSON.stringify(surrogateKeys)}`,
      );
    }
  } catch (err) {
    const message =
      err instanceof Error
        ? err.message
        : `Error performing Fastly purge for surrogateKeys ${JSON.stringify(surrogateKeys)}`;

    captureException(err, {
      tags: {
        context,
      },
      extra: {
        message,
      },
    });
  }
};
